import type { VisibilityCondition, PropertyValueData } from './types';

/**
 * Resolves template variables in a property identifier.
 * Supports `{objectIdentifier}` which is replaced with the given objectIdentifier.
 */
function resolveIdentifier(identifier: string, objectIdentifier?: string): string {
  if (!objectIdentifier) return identifier;
  return identifier.replace('{objectIdentifier}', objectIdentifier);
}

/**
 * Evaluates whether a property should be visible based on its visibility conditions.
 * All conditions must pass (AND logic). Within a single condition, an array of values
 * means the controlling property must match ANY of them (OR logic).
 *
 * @param conditions - The visibility conditions to evaluate (undefined/empty = always visible)
 * @param valueMap - Map of property identifiers to their current values
 * @param objectIdentifier - The current object's identifier, used to resolve `{objectIdentifier}` templates
 * @returns true if the property should be visible
 */
export function evaluateVisibilityConditions(conditions: VisibilityCondition[] | undefined, valueMap: Record<string, PropertyValueData>, objectIdentifier?: string): boolean {
  if (!conditions || conditions.length === 0) return true;

  return conditions.every((condition) => {
    const resolvedId = resolveIdentifier(condition.propertyIdentifier, objectIdentifier);
    const currentValue = valueMap[resolvedId];
    const expectedValues = Array.isArray(condition.value) ? condition.value : [condition.value];

    const matches = expectedValues.some((expected) => JSON.stringify(currentValue) === JSON.stringify(expected));

    return condition.negate ? !matches : matches;
  });
}

/**
 * Extracts all property identifiers referenced in visibility conditions.
 * Used to subscribe to these properties for re-rendering when conditions change.
 * Returns raw identifiers (with `{objectIdentifier}` unresolved).
 */
export function getConditionDependencyIdentifiers(conditions: VisibilityCondition[] | undefined): string[] {
  if (!conditions || conditions.length === 0) return [];
  return conditions.map((c) => c.propertyIdentifier);
}

/**
 * Resolves condition dependency identifiers for a specific object.
 */
export function resolveConditionDependencyIdentifiers(conditions: VisibilityCondition[] | undefined, objectIdentifier: string): string[] {
  if (!conditions || conditions.length === 0) return [];
  return conditions.map((c) => resolveIdentifier(c.propertyIdentifier, objectIdentifier));
}
